import { useCallback } from 'react';
import { CANVAS_SIZE } from '../../constants';

export const useNodeInteraction = ({
  nodes, setNodes, connections, setConnections,
  selectedNode, setSelectedNode,
  selectedNodes, setSelectedNodes,
  modalNode, setModalNode,
  setEditNode,
  setEditTitle, setEditSummary, setEditDescription,
  setColorPickerNode, setSelectedColor,
  scrollContainerRef, zoom,
  updateNode, deleteNode,
  setMessages, setConfirmDialog,
  infoGathering
}) => {

  const handleNodeClick = useCallback((e, node) => {
    e.stopPropagation();
    if (selectedNodes.length > 1) return;
    setSelectedNodes(prev => prev[0] === node.id ? [] : [node.id]);
    if (selectedNode?.id && selectedNode.id !== node.id) {
      setSelectedNode(null);
    }
    setModalNode(null);
  }, [selectedNodes, selectedNode, setSelectedNodes, setSelectedNode, setModalNode]);

  const handleFocusNode = useCallback((node) => {
    setSelectedNode(prev => prev?.id === node.id ? null : node);
  }, [setSelectedNode]);

  const handleMarkNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) updateNode(nodeId, { marked: !node.marked });
  }, [nodes, updateNode]);

  const handleEditNode = useCallback((node) => {
    setEditNode(node);
    setEditTitle(node.title);
    setEditSummary(node.summary);
    setEditDescription(node.description || '');
  }, [setEditNode, setEditTitle, setEditSummary, setEditDescription]);

  const handleColorNode = useCallback((node) => {
    setColorPickerNode(node);
    setSelectedColor(node.color || (node.type === 'problem' ? '#f87171' : '#22c55e'));
  }, [setColorPickerNode, setSelectedColor]);

  const handleAddNode = useCallback((explicitParent, newNodePositionRef, selectedNodeForNewRef) => {
    selectedNodeForNewRef.current = explicitParent || selectedNode || modalNode;
    const container = scrollContainerRef.current;
    newNodePositionRef.current = container
      ? {
          x: (container.scrollLeft + container.clientWidth / 2) / zoom,
          y: (container.scrollTop + container.clientHeight / 2) / zoom,
        }
      : { x: CANVAS_SIZE / 2, y: CANVAS_SIZE / 2 };
    const defaultType = nodes.some(n => n.type === 'problem') ? 'solution' : 'problem';
    setEditNode({ id: '__new__', type: defaultType, title: '', summary: '', description: '' });
    setEditTitle('');
    setEditSummary('');
    setEditDescription('');
  }, [nodes, scrollContainerRef, zoom, selectedNode, modalNode, setEditNode, setEditTitle, setEditSummary, setEditDescription]);

  const handleBulkDelete = useCallback(() => {
    if (selectedNodes.length === 0) return;
    const nodesToDelete = selectedNodes.filter(nodeId => !connections.some(conn => conn.from === nodeId));
    if (nodesToDelete.length === 0) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Tutti i nodi selezionati hanno figli — elimina prima quelli.' }]);
      return;
    }
    const skipped = selectedNodes.length - nodesToDelete.length;
    const message = skipped > 0 ? `Eliminare ${nodesToDelete.length} nodi? (${skipped} con figli verranno saltati)` : `Eliminare ${nodesToDelete.length} nodi?`;
    setConfirmDialog({
      message, danger: true,
      onConfirm: () => {
        setNodes(prev => prev.filter(n => !nodesToDelete.includes(n.id)));
        setConnections(prev => prev.filter(c => !nodesToDelete.includes(c.to)));
        setSelectedNodes([]);
      },
    });
  }, [selectedNodes, connections, setNodes, setConnections, setSelectedNodes, setMessages, setConfirmDialog]);

  const handleDeleteNode = useCallback((node) => {
    if (connections.some(conn => conn.from === node.id)) {
      setMessages(prev => [...prev, { role: 'assistant', content: `"${node.title}" ha dei figli — elimina prima quelli.` }]);
      return;
    }
    setConfirmDialog({
      message: `Eliminare "${node.title}"?`, danger: true,
      onConfirm: () => deleteNode(node.id, connections),
    });
  }, [deleteNode, connections, setMessages, setConfirmDialog]);

  const handleStartInfoGathering = useCallback((node) => {
    setModalNode(null);
    infoGathering.startInfoGathering(node);
  }, [infoGathering, setModalNode]);

  return {
    handleNodeClick,
    handleFocusNode,
    handleMarkNode,
    handleEditNode,
    handleColorNode,
    handleAddNode,
    handleBulkDelete,
    handleDeleteNode,
    handleStartInfoGathering
  };
};
